// schemaindex — derived, case-insensitive lookups over the completion
// `databases` payload, shared by the JOIN-from-FK completion source and the
// conservative linter. Pure (no Solid/CM deps); unit-tested.
//
// The payload shape (per db): { database, tables:[{name, columns:[{name,type}],
// keys, foreignKeys:[{columns,refSchema,refTable,refCols}]}], views:[…] }.

// warm reports whether the schema cache is loaded + populated + not mid-refresh
// — the gate the linter / JOIN source check so a COLD cache never misfires
// (an unknown table during loading isn't "wrong", just not-yet-known).
export function warm(state) {
  return !!(state && state.loaded && !state.building && (state.databases || []).length > 0);
}

// buildIndex → Map<dbLower, { name, tables: Map<tableLower, {name, cols:Set<lower>, fks:[]}> }>.
// Views are indexed alongside tables (both are valid FROM targets); views carry
// no FKs.
export function buildIndex(databases) {
  const dbs = new Map();
  for (const db of databases || []) {
    if (!db || !db.database) continue;
    const tmap = new Map();
    const addT = (t, withFks) => {
      if (!t || !t.name) return;
      tmap.set(t.name.toLowerCase(), {
        name: t.name,
        cols: new Set((t.columns || []).map((c) => String(c.name).toLowerCase())),
        fks: withFks ? (t.foreignKeys || []) : [],
      });
    };
    for (const t of (db.tables || [])) addT(t, true);
    for (const v of (db.views || [])) addT(v, false);
    dbs.set(db.database.toLowerCase(), { name: db.database, tables: tmap });
  }
  return dbs;
}

export function hasDb(index, db) { return index.has(String(db || '').toLowerCase()); }

export function tableEntry(index, db, table) {
  const d = index.get(String(db || '').toLowerCase());
  return d ? d.tables.get(String(table || '').toLowerCase()) || null : null;
}

export function knownTable(index, db, table) { return !!tableEntry(index, db, table); }

const lc = (x, def) => String(x || def || '').toLowerCase();

// fkTargetMatches: does FK `fk` (owned by a table in ownerDb) reference `target`
// ({db, table})? Empty fk.refSchema means same DB as the FK's owner.
function fkTargetMatches(fk, ownerDb, target, defaultDb) {
  if (lc(fk.refTable) !== lc(target.table)) return false;
  return lc(fk.refSchema || ownerDb, defaultDb) === lc(target.db, defaultDb);
}

// joinConditions returns candidate ON-clause strings linking the just-JOINed
// table `joined` to each of the `others` already in the FROM/JOIN list, in
// BOTH directions (joined's FK → other, and other's FK → joined). Refs are
// `{db, table, alias}`; the qualifier used is alias || table. Multi-column FKs
// join with AND. Deduped, stable order.
export function joinConditions(index, defaultDb, joined, others) {
  if (!joined || !joined.table) return [];
  const out = [];
  const seen = new Set();
  const qual = (r) => r.alias || r.table;
  const push = (lAlias, lCols, rAlias, rCols) => {
    if (!lCols.length || lCols.length !== rCols.length) return;
    const cond = lCols.map((c, i) => `${lAlias}.${c} = ${rAlias}.${rCols[i]}`).join(' AND ');
    if (!seen.has(cond)) { seen.add(cond); out.push(cond); }
  };
  const jEntry = tableEntry(index, joined.db || defaultDb, joined.table);
  for (const o of (others || [])) {
    if (!o || !o.table) continue;
    const oEntry = tableEntry(index, o.db || defaultDb, o.table);
    if (jEntry) {
      for (const fk of jEntry.fks) {
        if (fkTargetMatches(fk, joined.db || defaultDb, o, defaultDb)) {
          push(qual(joined), fk.columns, qual(o), fk.refCols);
        }
      }
    }
    if (oEntry) {
      for (const fk of oEntry.fks) {
        if (fkTargetMatches(fk, o.db || defaultDb, joined, defaultDb)) {
          push(qual(o), fk.columns, qual(joined), fk.refCols);
        }
      }
    }
  }
  return out;
}
